import React, { useState, useMemo } from 'react';
import { X, ChevronRight } from 'lucide-react';
import type { RepoStackAnalysis, KBNode, KBEdge, DetectedTool } from '../types';
import { CAT_COLORS, TIER_COLORS, STAGE_LABELS } from '../types';

interface ToolPanelProps {
  toolId: string | null;
  data: RepoStackAnalysis;
  onClose: () => void;
  onSelectTool: (id: string) => void;
}

// ── Helpers ──

function buildIndexes(data: RepoStackAnalysis) {
  const nodeMap: Record<string, KBNode> = {};
  for (const n of data.graph.nodes) nodeMap[n.id] = n;

  const detectedMap: Record<string, DetectedTool> = {};
  for (const d of data.detectedTools) detectedMap[d.id] = d;

  const recommendedIds = new Set(data.recommendations.map((r) => r.suggested));

  // Edge index (excluding language/ecosystem edges)
  const relsIndex: Record<string, KBEdge[]> = {};
  for (const e of data.graph.edges) {
    if (e.type === 'language' || e.type === 'ecosystem') continue;
    (relsIndex[e.source] ||= []).push(e);
    (relsIndex[e.target] ||= []).push(e);
  }

  return { nodeMap, detectedMap, recommendedIds, relsIndex };
}

const SIGNAL_LABELS: Record<string, string> = {
  files: 'Found file',
  directories: 'Found directory',
  dependencies: 'Listed as dependency',
  config_keys: 'In configuration',
  file_extensions: 'File type detected',
  content_patterns: 'Code pattern found',
  markers: 'Reference found',
};

const TIER_LABEL: Record<string, string> = {
  detected: 'Confirmed in your project',
  likely: 'Likely used in your project',
  possible: 'Detected with lower confidence',
};

// ── Collapsible Section ──

const CollapsibleSection: React.FC<{
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}> = ({ title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-200 dark:border-slate-700/50 rounded-lg overflow-hidden mb-3">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3.5 py-2.5 text-xs font-semibold text-secondary bg-slate-100/60 dark:bg-slate-800/40 hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-colors"
      >
        {title}
        <ChevronRight
          className={`w-3 h-3 text-tertiary transition-transform ${open ? 'rotate-90' : ''}`}
        />
      </button>
      <div
        className={`overflow-hidden transition-[max-height] duration-300 ${open ? 'max-h-[600px]' : 'max-h-0'}`}
      >
        {children}
      </div>
    </div>
  );
};

// ── Main Component ──

export const ToolPanel: React.FC<ToolPanelProps> = ({ toolId, data, onClose, onSelectTool }) => {
  const { nodeMap, detectedMap, recommendedIds, relsIndex } = useMemo(
    () => buildIndexes(data),
    [data],
  );

  const node = toolId ? nodeMap[toolId] : null;
  const detection = toolId ? detectedMap[toolId] : undefined;
  const isRecommended = toolId ? !detection && recommendedIds.has(toolId) : false;
  const recommendation = data.recommendations.find((r) => r.suggested === toolId);
  const replaceRec = data.recommendations.find((r) => r.current === toolId);
  const adoption = toolId ? data.adoption[toolId] : undefined;

  const catColor = node ? CAT_COLORS[node.category] || '#888' : '#888';

  // Related tools grouped by edge type
  const relGroups = useMemo(() => {
    if (!toolId) return {};
    const groups: Record<string, string[]> = {};
    for (const e of relsIndex[toolId] || []) {
      const otherId = e.source === toolId ? e.target : e.source;
      if (!groups[e.type]) groups[e.type] = [];
      if (!groups[e.type].includes(otherId)) groups[e.type].push(otherId);
    }
    return groups;
  }, [toolId, relsIndex]);

  const [fixApplied, setFixApplied] = useState(false);

  // Reset fix state when tool changes
  React.useEffect(() => setFixApplied(false), [toolId]);

  if (!node) {
    return (
      <div
        className={`fixed right-0 top-[77px] bottom-0 w-[370px] bg-white/95 dark:bg-slate-950/95 backdrop-blur-xl border-l border-slate-200 dark:border-slate-700/50 z-[90] transform transition-transform duration-300 ease-out ${
          toolId ? 'translate-x-0' : 'translate-x-full'
        }`}
      />
    );
  }

  const actionRec = replaceRec;

  return (
    <div
      className={`fixed right-0 top-[77px] bottom-0 w-[370px] bg-white/95 dark:bg-slate-950/95 backdrop-blur-xl border-l border-slate-200 dark:border-slate-700/50 p-6 overflow-y-auto z-[90] transform transition-transform duration-300 ease-out ${
        toolId ? 'translate-x-0' : 'translate-x-full'
      }`}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-3.5 right-3.5 w-7 h-7 flex items-center justify-center rounded-md border border-slate-200 dark:border-slate-700/50 bg-slate-100 dark:bg-slate-800 text-secondary hover:text-primary hover:border-slate-300 dark:hover:border-slate-600 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>

      {/* Name */}
      <h3
        className="text-lg font-semibold mb-1.5 pr-10"
        style={{ color: isRecommended ? '#2dd4bf' : catColor }}
      >
        {node.name}
      </h3>

      {/* Badges */}
      <div className="flex flex-wrap gap-1.5 mb-3.5">
        <span
          className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide"
          style={{ backgroundColor: catColor + '18', color: catColor }}
        >
          {node.category.replace(/_/g, ' ')}
        </span>
        <span className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-slate-100 dark:bg-slate-800 text-secondary">
          {STAGE_LABELS[node.sdlcStage] || node.sdlcStage}
        </span>
        {detection && detection.tier !== 'possible' && (
          <span
            className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide"
            style={{
              backgroundColor: TIER_COLORS[detection.tier] + '18',
              color: TIER_COLORS[detection.tier],
            }}
          >
            {detection.tier}
          </span>
        )}
        {isRecommended && (
          <span className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-teal-500/10 text-teal-500 dark:text-teal-400">
            suggested
          </span>
        )}
      </div>

      {/* Status + confidence */}
      {detection && (
        <>
          <div className="flex items-center gap-2 text-[13px] font-medium mb-3.5">
            <span
              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: TIER_COLORS[detection.tier] }}
            />
            <span style={{ color: TIER_COLORS[detection.tier] }}>{TIER_LABEL[detection.tier]}</span>
          </div>
          <div className="mb-4">
            <div className="text-[10px] text-tertiary uppercase tracking-wider mb-1">
              Detection confidence
            </div>
            <div className="w-full h-2 rounded-full bg-slate-200/50 dark:bg-slate-700/30 overflow-hidden">
              <div
                className="h-full rounded-full transition-[width] duration-400"
                style={{
                  width: `${detection.confidence * 100}%`,
                  backgroundColor: TIER_COLORS[detection.tier],
                }}
              />
            </div>
            <div
              className="text-xs font-mono font-semibold mt-1"
              style={{ color: TIER_COLORS[detection.tier] }}
            >
              {Math.round(detection.confidence * 100)}%
            </div>
          </div>
        </>
      )}

      {isRecommended && (
        <div className="flex items-center gap-2 text-[13px] font-medium mb-3.5">
          <span className="w-2.5 h-2.5 rounded-full border-2 border-dashed border-teal-400 flex-shrink-0" />
          <span className="text-teal-500 dark:text-teal-400">Not detected in your project</span>
        </div>
      )}

      {/* Description */}
      {node.description && (
        <p className="text-[13px] text-secondary leading-relaxed mb-4">{node.description}</p>
      )}

      {/* Fix Now CTA */}
      {actionRec && (
        <div className="mb-4">
          <button
            onClick={() => setFixApplied(true)}
            disabled={fixApplied}
            className={`w-full py-2.5 px-4 rounded-lg text-[13px] font-semibold transition-all ${
              fixApplied
                ? 'bg-emerald-500/10 text-emerald-500 cursor-default'
                : 'bg-gradient-to-r from-teal-600 to-teal-500 text-white shadow-md shadow-teal-600/30 hover:from-teal-500 hover:to-teal-400 hover:-translate-y-0.5'
            }`}
          >
            {fixApplied ? '\u2713 Fixed' : 'Fix Now'}
          </button>
        </div>
      )}

      {/* KB Adoption */}
      {adoption != null && (
        <div className="mb-4">
          <div className="text-[10px] text-tertiary uppercase tracking-wider mb-1">KB Adoption</div>
          <div className="w-full h-1.5 rounded-full bg-slate-200/30 dark:bg-slate-700/30 overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                width: `${adoption}%`,
                backgroundColor: adoption > 50 ? '#34d399' : adoption > 25 ? '#2dd4bf' : '#64748b',
              }}
            />
          </div>
          <div className="text-xs text-secondary font-mono mt-1">
            {adoption}% of scanned repos use this
          </div>
        </div>
      )}

      {/* Replacement recommendation callout */}
      {replaceRec && (
        <div className="p-3.5 rounded-lg border border-amber-400/15 bg-amber-500/[0.04] mb-3.5">
          <div className="text-[10px] font-bold uppercase tracking-wider text-amber-500 mb-1">
            Consider replacing
          </div>
          <div className="text-xs text-secondary leading-relaxed">{replaceRec.reason}</div>
          {nodeMap[replaceRec.suggested] && (
            <button
              onClick={() => onSelectTool(replaceRec.suggested)}
              className="mt-2 px-3 py-1 rounded-md text-xs bg-teal-500/10 text-teal-500 dark:text-teal-400 hover:bg-teal-500/20 transition-colors"
            >
              View {nodeMap[replaceRec.suggested].name} &rarr;
            </button>
          )}
        </div>
      )}

      {/* Gap context card — ghost node only */}
      {recommendation && isRecommended && (
        <div className="p-3.5 rounded-lg border border-teal-500/25 bg-teal-500/[0.04] mb-3.5">
          <div className="text-[10px] font-bold uppercase tracking-wider text-teal-500 dark:text-teal-400 mb-2">
            Gap Analysis
          </div>
          <div className="flex items-center gap-3 mb-2">
            <div>
              <div className="text-[9px] text-tertiary uppercase tracking-wider">Priority</div>
              <div
                className={`text-xs font-bold capitalize ${
                  recommendation.priority === 'high'
                    ? 'text-red-400'
                    : recommendation.priority === 'low'
                      ? 'text-emerald-400'
                      : 'text-amber-400'
                }`}
              >
                {recommendation.priority}
              </div>
            </div>
            {recommendation.effort && (
              <div>
                <div className="text-[9px] text-tertiary uppercase tracking-wider">Effort</div>
                <div className="text-xs text-secondary capitalize">{recommendation.effort}</div>
              </div>
            )}
          </div>
          <div className="text-xs text-secondary leading-relaxed">{recommendation.reason}</div>
        </div>
      )}

      {/* Detection signals */}
      {detection?.signals && detection.signals.length > 0 && (
        <CollapsibleSection title="How we detected it">
          {detection.signals.map((s, i) => (
            <div
              key={i}
              className="flex items-center gap-2.5 px-3.5 py-1.5 text-xs border-t border-slate-200/50 dark:border-slate-700/30"
            >
              <span className="text-secondary min-w-[110px]">
                {SIGNAL_LABELS[s.method] || s.method}
              </span>
              <span className="text-primary font-mono text-xs">{s.match}</span>
            </div>
          ))}
        </CollapsibleSection>
      )}

      {/* Info grid */}
      <div className="grid grid-cols-2 gap-2 mb-3.5">
        {detection?.version && (
          <div className="p-2 rounded-md bg-slate-100/60 dark:bg-slate-800/50">
            <div className="text-[9px] text-tertiary uppercase tracking-wider">Version</div>
            <div className="text-[13px] text-primary font-mono">{detection.version}</div>
          </div>
        )}
        {node.latestVersion && (
          <div className="p-2 rounded-md bg-slate-100/60 dark:bg-slate-800/50">
            <div className="text-[9px] text-tertiary uppercase tracking-wider">Latest</div>
            <div className="text-[13px] text-primary font-mono">{node.latestVersion}</div>
          </div>
        )}
        {node.license && (
          <div className="p-2 rounded-md bg-slate-100/60 dark:bg-slate-800/50">
            <div className="text-[9px] text-tertiary uppercase tracking-wider">License</div>
            <div className="text-[13px] text-primary font-mono">{node.license}</div>
          </div>
        )}
        <div className="p-2 rounded-md bg-slate-100/60 dark:bg-slate-800/50">
          <div className="text-[9px] text-tertiary uppercase tracking-wider">Connections</div>
          <div className="text-[13px] text-primary font-mono">{node.connections}</div>
        </div>
      </div>

      {/* Tags */}
      {node.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3.5">
          {node.tags.map((t) => (
            <span
              key={t}
              className="px-2.5 py-0.5 rounded text-xs bg-slate-100 dark:bg-slate-800/50 text-secondary"
            >
              {t}
            </span>
          ))}
        </div>
      )}

      {/* Related tools */}
      {Object.keys(relGroups).length > 0 && (
        <CollapsibleSection title="Related tools">
          {Object.entries(relGroups).map(([type, ids]) => (
            <div key={type} className="px-3.5 py-1.5">
              <div className="text-[10px] text-tertiary uppercase tracking-wide mb-1">
                {type.replace(/_/g, ' ')} ({ids.length})
              </div>
              <div className="flex flex-wrap gap-1">
                {ids.slice(0, 8).map((id) => {
                  const n = nodeMap[id];
                  if (!n) return null;
                  const isDetected = !!detectedMap[id];
                  const isSuggested = recommendedIds.has(id);
                  return (
                    <button
                      key={id}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectTool(id);
                      }}
                      className={`px-2.5 py-0.5 rounded text-xs transition-colors ${
                        isDetected
                          ? 'bg-emerald-500/[0.08] text-emerald-500'
                          : isSuggested
                            ? 'bg-teal-500/10 text-teal-500 dark:text-teal-400'
                            : 'bg-slate-100 dark:bg-slate-800/50 text-secondary hover:text-primary'
                      }`}
                    >
                      {n.name}
                    </button>
                  );
                })}
                {ids.length > 8 && (
                  <span className="text-[10px] text-tertiary px-1.5 py-0.5">
                    +{ids.length - 8} more
                  </span>
                )}
              </div>
            </div>
          ))}
        </CollapsibleSection>
      )}
    </div>
  );
};
